import type { LoaderFunctionArgs } from "react-router-dom";
import type { CurvesFormState } from "../../types";
import { getDefaultDataFromQueryParams } from "./getDefaultDataFromQueryParams.ts";
import { getPrimaryDataProvider } from "./getPrimaryDataProvider.ts";

/**
 * Retrieves the default curves data based on the request's URL search parameters.
 *
 * This function:
 * - Parses the URL search parameters from the request.
 * - Extracts default data using `getDefaultDataFromQueryParams`.
 * - Determines the `apiInterest` flag, `primaryProduct`, and `primaryDataProvider` values.
 *
 * @param {LoaderFunctionArgs["request"]} request - The request object containing the URL.
 * @returns {CurvesFormState} The default curves data including API interest, primary product, and provider.
 *
 * @example
 * const request = new Request("https://example.com?apiInterest=true&primaryProduct=Oil");
 * const curvesData = getDefaultCurvesData(request);
 * console.log(curvesData);
 */
export function getDefaultCurvesData(request: LoaderFunctionArgs["request"]): CurvesFormState {
  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.search);

  const result = getDefaultDataFromQueryParams(request);

  return {
    ...result,
    apiInterest: searchParams.get("apiInterest") === "true",
    primaryDataProvider: getPrimaryDataProvider(searchParams),
  };
}
